/**
 * The preview's page size menu (decision 11; part 15c): a button naming the chosen device, and a panel sliding down over
 * the pages with the devices under their brands, a star beside each, and the starred ones first in a collapsible group.
 * A disclosure, not a `menu`: a row holds two buttons, which a menu item may not. The panel is out of the bar's flow, so
 * opening it changes no page's room and lays nothing out again.
 */
import { createEffect, createMemo, createSignal, onCleanup } from 'solid-js';
import type { ProfileId } from '@cockatrice/layout';
import type { Messages } from '../i18n/en.ts';
import { BRANDS, deviceOn, DEVICES } from '../preview/devices.ts';
import type { Brand, Device } from '../preview/devices.ts';
import { focusOn } from './announce.ts';
import Icon from '../ui/Icon.ts';
import { component } from '../ui/solid.ts';
import { each, el, when } from '../ui/dom.ts';
import type { Child, On } from '../ui/dom.ts';
import type { Shell } from './shell.ts';

/** Each profile's name, for the settings page and for a profile picked with no device named (DV2, DV3). */
export const PROFILE_NAMES: Readonly<Record<ProfileId, keyof Messages & `preview.profile.${string}`>> = Object.freeze({
  'generic-6in': 'preview.profile.generic-6in',
  kindle: 'preview.profile.kindle',
  phone: 'preview.profile.phone',
  tablet: 'preview.profile.tablet',
  'eink-8in': 'preview.profile.eink-8in',
  'eink-10in': 'preview.profile.eink-10in',
  'pdf-a5': 'preview.profile.pdf-a5',
});

/** Each group's heading. */
const BRAND_NAMES: Readonly<Record<Brand, keyof Messages & `preview.brand.${string}`>> = Object.freeze({
  general: 'preview.brand.general',
  kindle: 'preview.brand.kindle',
  kobo: 'preview.brand.kobo',
  apple: 'preview.brand.apple',
  android: 'preview.brand.android',
  eink: 'preview.brand.eink',
});

/** The pane's first control: the menu's button. */
export const PROFILE_CHOSEN = '.preview-device-button';

/** DV14: the presses that closed the panel: they dismiss it and do nothing else, so a press on a page doesn't also turn it. */
export const dismissals = new WeakSet<Event>();

/** DV16: CSS pixels the open panel keeps clear of the pane's foot edge, past its slide. */
const PANEL_MARGIN = 16;

/** The button and panel of the page size menu; `paneBottom` is where the pane ends, which the panel stays inside (DV16). */
export function DeviceMenu(shell: Shell, paneBottom: () => number): Child {
  const { t, workspace } = shell;
  const [open, setOpen] = createSignal(false);
  // DV17: whether the group is open, kept while the pane is here, so closing the menu doesn't undo a collapse.
  const [groupOpen, setGroupOpen] = createSignal(true);
  const settings = workspace.settings;
  const chosen = createMemo(() => deviceOn(settings().previewDevice, settings().previewProfile));
  const label = (): string => {
    const device = chosen();
    return device === null ? t(PROFILE_NAMES[settings().previewProfile]) : t(device.name);
  };
  const favourites = createMemo(() => DEVICES.filter((device) => settings().favouriteDevices.includes(device.id)));
  // The scrolling panel and the list under the favourites: set by their refs, which run before any star can be pressed.
  let panel = document.documentElement;
  let brands = document.documentElement;
  const close = (): void => {
    setOpen(false);
    focusOn(shell, PROFILE_CHOSEN);
  };
  const outside = (target: EventTarget | null): boolean => !(target instanceof Element && target.closest('.preview-device') !== null);
  // DV11: Escape and a press outside, only while open. In the capture phase, so Escape closes this and nothing else, and a
  // press is marked before anything under it sees it (DV14).
  createEffect(() => {
    if (!open()) return;
    // DV16: the panel ends inside the pane and the window, however far down the bar ends, and when the phone turns.
    const fit = (): void => {
      panel.style.setProperty('--panel-room', `${String(Math.min(paneBottom(), document.documentElement.clientHeight) - panel.getBoundingClientRect().top - PANEL_MARGIN)}px`);
    };
    fit();
    const pressed = (event: PointerEvent): void => {
      if (!outside(event.target)) return;
      dismissals.add(event);
      setOpen(false);
    };
    // DV15: the focus leaving (Tab past the last star) closes the panel, so it never goes under it.
    const focused = (event: FocusEvent): void => {
      if (outside(event.target)) setOpen(false);
    };
    const key = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      close();
    };
    window.addEventListener('resize', fit);
    document.addEventListener('pointerdown', pressed, true);
    document.addEventListener('focusin', focused);
    document.addEventListener('keydown', key, true);
    onCleanup(() => {
      window.removeEventListener('resize', fit);
      document.removeEventListener('pointerdown', pressed, true);
      document.removeEventListener('focusin', focused);
      document.removeEventListener('keydown', key, true);
    });
  });
  // DV18: Up and Down between the rows, as the design's menu has them, past a collapsed group, round at the ends; from
  // anywhere that isn't a row (the group's summary, the button), Down is the first row and Up the last.
  const move = (step: 1 | -1): void => {
    const rows = [...panel.querySelectorAll<HTMLElement>('.preview-device-choose')].filter((row) => row.closest('details:not([open])') === null);
    const from = rows.findIndex((row) => row.parentElement?.contains(document.activeElement));
    const target = ((from < 0 && step < 0 ? 0 : from) + step + rows.length) % rows.length;
    for (const [index, row] of rows.entries()) if (index === target) row.focus();
  };
  const stepOf = (key: string): 1 | -1 | 0 => (key === 'ArrowDown' ? 1 : key === 'ArrowUp' ? -1 : 0);
  const arrows = (event: On<KeyboardEvent, HTMLElement>): void => {
    const step = stepOf(event.key);
    if (step === 0) return;
    event.preventDefault();
    setOpen(true);
    move(step);
  };
  const star = (device: Device, event: On<MouseEvent, HTMLElement>): void => {
    const inGroup = event.currentTarget.closest('details') !== null;
    const before = brands.getBoundingClientRect().top;
    const { favouriteDevices } = settings();
    void workspace.updateSettings({ favouriteDevices: favouriteDevices.includes(device.id) ? favouriteDevices.filter((id) => id !== device.id) : [...favouriteDevices, device.id] });
    // DV9: the group grew or shrank above the brands; the star stays under the pointer.
    panel.scrollTop += brands.getBoundingClientRect().top - before;
    // DV9: a star in the group may go with its row; the same device's star under its brand keeps the focus.
    if (inGroup) focusOn(shell, `.preview-device-brands [data-star="${device.id}"]`);
  };
  const row = (device: Device): Child =>
    el(
      'li',
      { class: 'preview-device-row' },
      el(
        'button',
        {
          type: 'button',
          class: 'preview-device-choose',
          // With no device named, the general entry on the same pages stands for what is shown.
          'aria-current': () => (chosen() === device || (chosen() === null && device.brand === 'general' && device.profile === settings().previewProfile) ? 'true' : undefined),
          onClick: () => {
            void workspace.updateSettings({ previewDevice: device.id, previewProfile: device.profile });
            close();
          },
        },
        t(device.name),
      ),
      el(
        'button',
        {
          type: 'button',
          class: 'preview-device-star',
          'data-star': device.id,
          'aria-label': t('preview.favourite', { device: t(device.name) }),
          'aria-pressed': () => String(settings().favouriteDevices.includes(device.id)),
          onClick: (event: On<MouseEvent, HTMLElement>) => {
            star(device, event);
          },
        },
        component(Icon, { name: 'star' }),
      ),
    );
  return el(
    'div',
    { class: 'preview-device' },
    el(
      'button',
      {
        type: 'button',
        class: 'preview-device-button',
        'aria-label': () => t('preview.deviceChosen', { device: label() }),
        'aria-expanded': () => String(open()),
        'aria-controls': 'preview-devices',
        onKeyDown: arrows,
        onClick: () => {
          setOpen(!open());
        },
      },
      el('span', { class: 'preview-device-name' }, label),
      component(Icon, { name: 'chevronDown' }),
    ),
    when(open, () =>
      el(
        'div',
        {
          id: 'preview-devices',
          class: 'preview-devices',
          role: 'group',
          'aria-label': t('preview.devices'),
          onKeyDown: arrows,
          ref: (element: HTMLElement) => {
            panel = element;
          },
        },
        when(
          () => favourites().length > 0,
          () =>
            el(
              'details',
              {
                class: 'preview-device-favourites',
                open: groupOpen(),
                onToggle: (event: On<Event, HTMLDetailsElement>) => {
                  setGroupOpen(event.currentTarget.open);
                },
              },
              el('summary', {}, el('span', {}, t('preview.favourites')), component(Icon, { name: 'chevronDown' })),
              el('ul', {}, each(favourites, row)),
            ),
        ),
        el(
          'div',
          {
            class: 'preview-device-brands',
            ref: (element: HTMLElement) => {
              brands = element;
            },
          },
          BRANDS.map((brand) =>
            el(
              'div',
              { role: 'group', 'aria-label': t(BRAND_NAMES[brand]) },
              el('p', { class: 'preview-device-brand', 'aria-hidden': 'true' }, t(BRAND_NAMES[brand])),
              el('ul', {}, DEVICES.filter((device) => device.brand === brand).map(row)),
            ),
          ),
        ),
      ),
    ),
  );
}
